import { SxProps, Theme } from '@mui/material/styles';
import { typographyClasses } from '@mui/material/Typography';
import { TypographyProps } from '@/components/atoms/Typography/types';
import palette from '@/theme/palette';

/**
 * Throws an error at runtime if sx contains fontSize, fontWeight, lineHeight, or color. Design system components
 * should not have key properties changed or color changed via sx properties.
 *
 * @param sx
 */
export const restrictedSxPropertiesError = (sx?: SxProps<Theme>) => {
  const hasFontSize = sx?.hasOwnProperty('fontSize');
  const hasFontWeight = sx?.hasOwnProperty('fontWeight');
  const hasLineHeight = sx?.hasOwnProperty('lineHeight');
  const hasColor = sx?.hasOwnProperty('color');
  const hasTextAlign = sx?.hasOwnProperty('textAlign');

  if (hasFontSize) {
    throw new Error('fontSize cannot be changed.');
  }

  if (hasFontWeight) {
    throw new Error('fontWeight cannot be changed.');
  }

  if (hasLineHeight) {
    throw new Error('lineHeight cannot be changed.');
  }

  if (hasColor) {
    throw new Error('color cannot be changed by sx properties. Use the color prop instead.');
  }

  if (hasTextAlign) {
    throw new Error('textAlign cannot be changed by sx properties. Use the align prop instead.');
  }
};

export const textOverflowSx: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [`&.${typographyClasses.caption}, &.${typographyClasses.overline}`]: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
};

export const getPaletteColor = (color: TypographyProps['color']) => {
  if (!color) return palette.light.text.primary;

  if (typeof color === 'function') {
    return color;
  }

  const textColorMap: Record<keyof typeof palette.light.text, string> = {
    primary: palette.light.text.primary,
    secondary: palette.light.text.secondary,
    disabled: palette.light.text.disabled,
    info: palette.light.text.info,
    success: palette.light.text.success,
    warning: palette.light.text.warning,
    error: palette.light.text.error,
  };

  if (Object.prototype.hasOwnProperty.call(textColorMap, color)) {
    return textColorMap[color as keyof typeof textColorMap];
  }

  return color;
};
